import { dayjs } from '@tunasong/models'
import type { Availability, ManyOfManyPoll, Persisted, PollResponse } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'
import { Button, Typography, UserAvatar } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { AvailabilityControl } from './availability-control.js'
import { formatWaitingOn, getSlotTally, getViableSlotIndices, getWaitingOn } from './availability.js'
import { formatSlotFullLabel, formatSlotLabel, getLiveSlotIndices, isPastSlot, isRespondByPassed } from './date-slot.js'
import styles from './date-slot-list.module.css'

export interface DateSlotListProps {
  poll: Persisted<ManyOfManyPoll>
  responses: Persisted<PollResponse>[]
  /** The current user's answers, keyed by option index. */
  myAvailability: Record<string, Availability>
  /** ACL-derived. The denominator in `3 of 5 available` and the basis for viability. */
  memberIds: string[]
  /** userId → display name, for the waiting-on line. Falls back to the userId. */
  memberNames?: Record<string, string>
  isLoading?: boolean
  readOnly?: boolean
  /** Injected in tests so "past" is deterministic. */
  now?: string
  /** Owner-only affordance; absent for respondents. */
  onSuggestMore?(): void
  onAnswer(idx: number, value: Availability): void
}

const skeletonRowCount = 3

/**
 * The respondent surface: a vertical list of slots, one decision per row.
 *
 * Not a When2meet respondent × slot grid — a grid shows every respondent's every answer at
 * the moment the user has exactly one job, and its cells are small targets where a full-width
 * row control is a large one. The matrix belongs in the Responses layout, where reading rather
 * than deciding is the task.
 *
 * This component must never branch on `datesNeeded` for the rows themselves: the respondent's
 * experience is identical whether the band needs one night or five.
 */
export const DateSlotList: FC<DateSlotListProps> = props => {
  const { poll, responses, myAvailability, memberIds, memberNames, isLoading, readOnly, onSuggestMore, now, onAnswer } =
    props

  const liveIndices = getLiveSlotIndices(poll)

  if (isLoading) {
    return (
      <div className={styles.container}>
        {Array.from({ length: liveIndices.length || skeletonRowCount }, (_, i) => (
          <div key={i} data-slot-skeleton className={styles.skeleton} />
        ))}
      </div>
    )
  }

  if (liveIndices.length === 0) {
    return <Typography variant="body1">Add the dates you&apos;re considering.</Typography>
  }

  const needed = poll.datesNeeded ?? 1
  const viable = getViableSlotIndices(poll, responses, memberIds.length)
  const waiting = formatWaitingOn(getWaitingOn(poll, memberIds, responses, now).map(id => memberNames?.[id] ?? id))

  /**
   * Viability is a statement about consensus, so it needs answers to be about anything. On a
   * brand-new poll the negative framing arrives before the question does — and `Suggest more
   * dates` becomes the loudest element on a page nobody has answered yet. Before the first
   * response the slots and controls are self-explanatory, and `Waiting on` already covers the
   * owner's "what now".
   */
  const shortfall =
    responses.length === 0
      ? undefined
      : viable.length === 0
        ? 'No date works for everyone yet.'
        : viable.length < needed
          ? `You need ${needed} dates; only ${viable.length} ${viable.length === 1 ? 'works' : 'work'} for everyone.`
          : undefined

  const respondByPassed = isRespondByPassed(poll.respondBy, now)

  if (readOnly && poll.closed) {
    const winners = liveIndices.filter(idx => poll.finalOption?.[idx])
    const labels = winners.map(idx => formatSlotLabel(poll.pollOptions[idx].start ?? ''))
    return (
      <div className={styles.container}>
        <Typography variant="subtitle1" className={styles.summary}>
          {labels.length === 1
            ? `Rehearsal set for ${labels[0]}`
            : `${labels.length} rehearsals set: ${labels.join(', ')}`}
        </Typography>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div data-slot-rows>
        {liveIndices.map(idx => {
          const slot = poll.pollOptions[idx]
          if (!isDateSlot(slot)) {
            return null
          }
          const past = isPastSlot(slot, now)
          const tally = getSlotTally(responses, idx)
          const yesVoters = responses.filter(r => r.availability?.[String(idx)] === 'yes')

          return (
            <div key={idx} className={past ? styles.rowPast : styles.row}>
              <span data-slot-label aria-hidden="true" className={styles.label} title={formatSlotFullLabel(slot)}>
                {formatSlotLabel(slot.start)}
              </span>
              {past ? <span className={styles.passed}>Passed</span> : null}

              <AvailabilityControl
                name={`${poll.id}-slot-${idx}`}
                legend={formatSlotFullLabel(slot)}
                value={myAvailability[String(idx)]}
                disabled={readOnly || past}
                onChange={value => onAnswer(idx, value)}
              />

              {/*
                Tally and avatars share one line so the avatar stack can never wrap to its own
                row on mobile, which made answered rows ~50px taller than unanswered ones and
                broke the vertical rhythm down the list.
              */}
              <span className={styles.meta}>
                <span className={styles.tally}>
                  <span>{`${tally.yes} of ${memberIds.length} available`}</span>
                  {/* Maybes are surfaced separately and never folded into the available count. */}
                  {tally.maybe > 0 ? <span className={styles.maybe}>{`${tally.maybe} maybe`}</span> : null}
                </span>

                <span className={styles.avatars}>
                  {yesVoters.map(voter => (
                    <UserAvatar key={voter.id} userId={voter.userId} className={styles.avatar} />
                  ))}
                </span>
              </span>
            </div>
          )
        })}
      </div>

      {respondByPassed ? (
        <Typography variant="caption" className={styles.notice}>
          {`Responses were due ${dayjs(poll.respondBy).format('dddd')}.`}
        </Typography>
      ) : null}

      {shortfall ? (
        <Typography variant="caption" className={styles.notice}>
          {shortfall}
        </Typography>
      ) : null}

      {shortfall && onSuggestMore ? <Button onClick={onSuggestMore}>Suggest more dates</Button> : null}

      {waiting ? (
        <Typography variant="caption" className={styles.notice}>
          {/*
            When nothing was truncated the visible line IS the full line, so render it once.
            Only a capped line needs the hidden copy, and duplicating identical text would
            make a screen reader announce it twice.
          */}
          {waiting.visible === waiting.full ? (
            <span data-waiting-full>{waiting.full}</span>
          ) : (
            <>
              <span aria-hidden="true">{waiting.visible}</span>
              <span data-waiting-full className={styles.visuallyHidden}>
                {waiting.full}
              </span>
            </>
          )}
        </Typography>
      ) : null}
    </div>
  )
}

export default DateSlotList
